/**
 * Серверный API-клиент.
 *
 * Используется ТОЛЬКО в Server Components / Server Actions / route handlers.
 * Для клиентских запросов есть отдельный helper в `api-client.ts`.
 *
 * Базовый URL — внутренний loopback 127.0.0.1:8000, чтобы не ходить через nginx
 * и не нагружать TLS-handshake для SSR.
 */

import type {
  ApiError,
  CategoryFacets,
  CategoryTreeNode,
  OrderCreateIn,
  OrderDetail,
  Page,
  ProductDetail,
  ProductShort,
} from "./api-types";

// На сервере → loopback. На клиенте — публичный домен (используется в api-client.ts).
const SERVER_BASE = process.env.STOK_API_INTERNAL ?? "http://127.0.0.1:8000";

class ApiException extends Error {
  constructor(public status: number, public body: ApiError | unknown) {
    super(typeof body === "object" && body && "message" in body ? String(body.message) : "api error");
  }
}

async function get<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${SERVER_BASE}${path}`, {
    // ISR-кэш: 1 мин (next.revalidate=60). On-demand invalidates через POST
    // /api/revalidate сразу после изменений в БД, этот таймер — fallback.
    next: { revalidate: 60, ...(init?.next ?? {}) },
    headers: { Accept: "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!r.ok) {
    let body: unknown;
    try {
      body = await r.json();
    } catch {
      body = await r.text();
    }
    throw new ApiException(r.status, body);
  }
  return r.json() as Promise<T>;
}

// --- Catalog ---

export const api = {
  categories: () => get<CategoryTreeNode[]>("/api/catalog/categories"),
  category: (slug: string) => get<CategoryTreeNode>(`/api/catalog/categories/${encodeURIComponent(slug)}`),
  products: (params: {
    category_slug?: string;
    page?: number;
    page_size?: number;
    sort?: "popular" | "price_asc" | "price_desc" | "name_asc" | "name_desc";
    price_min?: number;
    price_max?: number;
    has_image?: boolean;
    /** Детерминированный шафл (sort=popular без category_slug). YYYYMMDD для ежедневного ротейта. */
    seed?: number;
    /** Список фасет-фильтров формата `<spec_key>|<v1>,<v2>` или
     *  `<spec_key>|<min>..<max>`. Бэкенд принимает несколько `f=…`. */
    f?: string[];
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.category_slug) qs.set("category_slug", params.category_slug);
    if (params.page) qs.set("page", String(params.page));
    if (params.page_size) qs.set("page_size", String(params.page_size));
    if (params.sort) qs.set("sort", params.sort);
    if (params.price_min != null) qs.set("price_min", String(params.price_min));
    if (params.price_max != null) qs.set("price_max", String(params.price_max));
    if (params.has_image) qs.set("has_image", "true");
    if (params.seed != null) qs.set("seed", String(params.seed));
    if (params.f) for (const v of params.f) qs.append("f", v);
    return get<Page<ProductShort>>(`/api/catalog/products?${qs.toString()}`);
  },
  facets: (slug: string) =>
    get<CategoryFacets>(
      `/api/catalog/categories/${encodeURIComponent(slug)}/facets`,
    ),
  product: (externalId: string) =>
    get<ProductDetail>(`/api/catalog/products/${encodeURIComponent(externalId)}`),
  productByUrl: (url: string) =>
    get<ProductDetail>(`/api/catalog/products/by-url?url=${encodeURIComponent(url)}`),
  search: (q: string, page = 1, pageSize = 20) => {
    const qs = new URLSearchParams({ q, page: String(page), page_size: String(pageSize) });
    return get<Page<ProductShort>>(`/api/search?${qs.toString()}`);
  },
  /** Autocomplete (search-as-you-type). До 8 товаров без подсчёта total — быстрее /search. */
  searchSuggest: (q: string, limit = 8) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return get<ProductShort[]>(`/api/search/suggest?${qs.toString()}`);
  },
  // Создание заявки: только POST, и для server-side прокси (Route Handler в Next).
  createOrder: async (data: OrderCreateIn, headers: Record<string, string> = {}): Promise<OrderDetail> => {
    const r = await fetch(`${SERVER_BASE}/api/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", ...headers },
      body: JSON.stringify(data),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new ApiException(r.status, body);
    }
    return r.json() as Promise<OrderDetail>;
  },
};

export { ApiException };

// --- Helpers для путей медиа ---

export function mediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  // path = "products/aa/abc.jpg" → "/media/products/aa/abc.jpg"
  return `/media/${path.replace(/^\/+/, "")}`;
}
